import { useState } from "react";
import { NotebookPen, Plus, Save, Trash2, Sparkles, FileDown, FileText, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { downloadOverlegPDF, downloadOverlegWord } from "@/lib/overlegExport";

const uid = () => Date.now() + "-" + Math.random().toString(36).slice(2, 7);
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const emptyOverleg = () => ({ id: uid(), titel: "", datum: today(), type: "", voorzitter: "", notulist: "", aanwezigen: "", afwezigen: "", agenda: "", acties: [], besluiten: "", volgende: "" });
const TYPES = ["HACCP-overleg", "Productieoverleg", "Teamoverleg", "Kwaliteitsoverleg", "MT-overleg", "Werkoverleg", "Directieoverleg", "Overig"];
const fmtDate = (s) => { try { const [y, m, d] = String(s).split("-"); return `${d}-${m}-${y}`; } catch (e) { return s; } };

const fieldCls = "h-9 w-full rounded-md border border-slate-300 px-2.5 text-sm focus:border-[#1C64B0] focus:outline-none focus:ring-1 focus:ring-[#1C64B0]";
const areaCls = "w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm focus:border-[#1C64B0] focus:outline-none focus:ring-1 focus:ring-[#1C64B0]";

export default function OverlegTab({ xl }) {
  const { canEdit, overlegNotulen, setOverlegNotulen } = xl;
  const lijst = Array.isArray(overlegNotulen) ? overlegNotulen : [];
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [aiNotes, setAiNotes] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const sorted = [...lijst].sort((a, b) => String(b.datum || "").localeCompare(String(a.datum || "")));
  const groups = [];
  sorted.forEach((o) => {
    const d = o.datum || "zonder datum";
    let g = groups.find((x) => x.datum === d);
    if (!g) { g = { datum: d, items: [] }; groups.push(g); }
    g.items.push(o);
  });

  const startNew = () => { const o = emptyOverleg(); setDraft(o); setSel(o.id); setAiNotes(""); };
  const edit = (o) => { setDraft({ ...o, acties: Array.isArray(o.acties) ? o.acties.map((a) => ({ ...a })) : [] }); setSel(o.id); setAiNotes(""); };
  const cancel = () => { setDraft(null); setSel(null); setAiNotes(""); };
  const upd = (f, v) => setDraft((d) => ({ ...d, [f]: v }));
  const updActie = (i, f, v) => setDraft((d) => ({ ...d, acties: d.acties.map((a, idx) => (idx === i ? { ...a, [f]: v } : a)) }));
  const addActie = () => setDraft((d) => ({ ...d, acties: [...(d.acties || []), { wat: "", wie: "", wanneer: "" }] }));
  const remActie = (i) => setDraft((d) => ({ ...d, acties: d.acties.filter((_, idx) => idx !== i) }));

  const save = () => {
    if (!draft) return;
    if (!draft.titel.trim() && !draft.type.trim()) { toast.error("Geef het overleg een titel of kies een type."); return; }
    if (!draft.datum) { toast.error("Kies een datum voor het overleg."); return; }
    const clean = { ...draft, titel: draft.titel.trim(), bewerkt: new Date().toISOString() };
    setOverlegNotulen((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.some((x) => x.id === clean.id) ? arr.map((x) => (x.id === clean.id ? clean : x)) : [...arr, clean];
    });
    toast.success("Notulen veilig opgeslagen.");
    cancel();
  };
  const remove = (o) => {
    if (!window.confirm(`Overleg "${o.titel || o.type || "zonder titel"}" van ${fmtDate(o.datum)} verwijderen?`)) return;
    setOverlegNotulen((prev) => (Array.isArray(prev) ? prev : []).filter((x) => x.id !== o.id));
    if (sel === o.id) cancel();
    toast.success("Overleg verwijderd.");
  };

  const runCopilot = async () => {
    if (!aiNotes.trim()) { toast.error("Plak eerst je losse notities in het AI-vak."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/overleg/copilot", { titel: draft?.titel || "", datum: draft?.datum || "", aanwezigen: draft?.aanwezigen || "", notities: aiNotes });
      setDraft((d) => {
        const bestaande = (d.acties || []).filter((a) => a && (a.wat || a.wie || a.wanneer));
        const aiActies = Array.isArray(data.acties) ? data.acties : [];
        return {
          ...d,
          agenda: (data.agenda && data.agenda.trim()) ? data.agenda : d.agenda,
          besluiten: (data.besluiten && data.besluiten.trim()) ? data.besluiten : d.besluiten,
          acties: [...bestaande, ...aiActies],
        };
      });
      toast.success("Notulen gestructureerd door de AI-copilot. Handmatige acties blijven behouden; controleer en pas aan waar nodig.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(false); }
  };

  const expPDF = (o) => { try { downloadOverlegPDF(`overleg-${o.datum}-${(o.titel || o.type || "notulen").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`, o); } catch (e) { toast.error("PDF maken mislukt: " + (e.message || e)); } };
  const expWord = (o) => { try { downloadOverlegWord(`overleg-${o.datum}-${(o.titel || o.type || "notulen").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.doc`, o); } catch (e) { toast.error("Word maken mislukt: " + (e.message || e)); } };

  return (
    <div data-testid="overleg-tab">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#1C64B0]/10 text-[#1C64B0]"><NotebookPen className="h-6 w-6" /></div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Overleg &amp; notulen</h1>
            <p className="text-sm text-slate-500">Leg vergaderingen vast, laat de AI-copilot losse notities netjes structureren en exporteer als PDF of Word. Alles wordt veilig per datum bewaard.</p>
          </div>
        </div>
        {canEdit && <Button data-testid="overleg-new-btn" onClick={startNew} className="bg-[#1C64B0] hover:bg-[#154E8A]"><Plus className="h-4 w-4 mr-1" /> Nieuw overleg</Button>}
      </div>

      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        {/* Lijst met opgeslagen overleggen, gegroepeerd per datum */}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="overleg-lijst">
          <div className="mb-3 text-sm font-bold text-slate-800">Opgeslagen overleggen</div>
          {groups.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400" data-testid="overleg-leeg">Nog geen notulen vastgelegd.</div>
          ) : (
            <div className="space-y-4">
              {groups.map((g) => (
                <div key={g.datum} data-testid="overleg-datumgroep">
                  <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">{g.datum === "zonder datum" ? g.datum : fmtDate(g.datum)}</div>
                  <div className="space-y-1.5">
                    {g.items.map((o) => (
                      <div key={o.id} className={`group flex items-center gap-2 rounded-md border px-2.5 py-2 transition-colors ${sel === o.id ? "border-[#1C64B0] bg-blue-50/60" : "border-slate-200 hover:bg-slate-50"}`} data-testid="overleg-item">
                        <button onClick={() => edit(o)} className="flex-1 text-left" data-testid="overleg-open">
                          <div className="text-sm font-semibold text-slate-800">{o.titel || o.type || "(zonder titel)"}</div>
                          <div className="text-[11px] text-slate-500">{[o.type, o.voorzitter].filter(Boolean).join(" · ") || "—"}</div>
                        </button>
                        <button onClick={() => expPDF(o)} title="PDF" className="rounded p-1 text-slate-400 hover:bg-white hover:text-[#1C64B0]" data-testid="overleg-item-pdf"><FileDown className="h-3.5 w-3.5" /></button>
                        {canEdit && <button onClick={() => remove(o)} title="Verwijderen" className="rounded p-1 text-slate-400 hover:bg-white hover:text-rose-500" data-testid="overleg-item-del"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Editor */}
        <div>
          {!draft ? (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="overleg-geen-selectie">
              <NotebookPen className="mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm text-slate-500">{canEdit ? "Kies een overleg links om te bekijken/bewerken, of klik op \"Nieuw overleg\"." : "Kies een overleg links om te bekijken."}</p>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="overleg-editor">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm font-bold text-slate-800">{lijst.some((x) => x.id === draft.id) ? "Overleg bewerken" : "Nieuw overleg"}</div>
                <button onClick={cancel} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" data-testid="overleg-close"><X className="h-4 w-4" /></button>
              </div>

              {/* AI-copilot */}
              {canEdit && (
                <div className="mb-5 rounded-lg border border-[#1C64B0]/30 bg-blue-50/40 p-3" data-testid="overleg-copilot">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#1C64B0]"><Sparkles className="h-4 w-4" /> AI-copilot — losse notities omzetten naar nette notulen</div>
                  <textarea data-testid="overleg-ai-notes" value={aiNotes} onChange={(e) => setAiNotes(e.target.value)} rows={4} className={areaCls} placeholder="Plak hier je ruwe/losse vergadernotities. De AI vult hieronder de besproken punten, actiepunten (wie/wat/wanneer) en besluiten in." />
                  <div className="mt-2 flex items-center gap-2">
                    <Button data-testid="overleg-ai-run" size="sm" disabled={aiBusy} onClick={runCopilot} className="bg-[#1C64B0] hover:bg-[#154E8A]">{aiBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Structureer met AI</Button>
                    <span className="text-[11px] text-slate-500">De AI vult alleen besproken punten, acties en besluiten in — je kop en namen blijven van jou.</span>
                  </div>
                </div>
              )}

              {/* Kopgegevens */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-500">Titel</label>
                  <Input data-testid="overleg-titel" value={draft.titel} disabled={!canEdit} onChange={(e) => upd("titel", e.target.value)} placeholder="bijv. HACCP-overleg week 24" className="h-9" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Datum</label>
                  <input type="date" data-testid="overleg-datum" value={draft.datum} disabled={!canEdit} onChange={(e) => upd("datum", e.target.value)} className={fieldCls} />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Type overleg</label>
                  <input list="overleg-types" data-testid="overleg-type" value={draft.type} disabled={!canEdit} onChange={(e) => upd("type", e.target.value)} placeholder="Kies of typ…" className={fieldCls} />
                  <datalist id="overleg-types">{TYPES.map((t) => <option key={t} value={t} />)}</datalist>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Voorzitter</label>
                  <Input data-testid="overleg-voorzitter" value={draft.voorzitter} disabled={!canEdit} onChange={(e) => upd("voorzitter", e.target.value)} placeholder="Naam" className="h-9" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Notulist</label>
                  <Input data-testid="overleg-notulist" value={draft.notulist} disabled={!canEdit} onChange={(e) => upd("notulist", e.target.value)} placeholder="Naam" className="h-9" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Aanwezigen</label>
                  <textarea data-testid="overleg-aanwezigen" value={draft.aanwezigen} disabled={!canEdit} onChange={(e) => upd("aanwezigen", e.target.value)} rows={2} className={areaCls} placeholder="Namen, komma-gescheiden" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Afwezig (met kennisgeving)</label>
                  <textarea data-testid="overleg-afwezigen" value={draft.afwezigen} disabled={!canEdit} onChange={(e) => upd("afwezigen", e.target.value)} rows={2} className={areaCls} placeholder="Namen, komma-gescheiden" />
                </div>
              </div>

              {/* Agenda */}
              <div className="mt-5">
                <label className="mb-1 block text-xs font-medium text-slate-500">Agenda / besproken punten</label>
                <textarea data-testid="overleg-agenda" value={draft.agenda} disabled={!canEdit} onChange={(e) => upd("agenda", e.target.value)} rows={6} className={areaCls} placeholder={"- Opening\n- Openstaande acties vorige keer\n- ..."} />
              </div>

              {/* Actiepunten */}
              <div className="mt-5">
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-500">Actiepunten</label>
                  {canEdit && <Button data-testid="overleg-add-actie" size="sm" variant="outline" onClick={addActie}><Plus className="h-3.5 w-3.5 mr-1" /> Actie toevoegen</Button>}
                </div>
                {(draft.acties || []).length === 0 ? (
                  <div className="rounded-md border border-dashed border-slate-200 py-4 text-center text-[13px] text-slate-400">Nog geen actiepunten.</div>
                ) : (
                  <div className="space-y-2">
                    <div className="hidden gap-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 sm:grid sm:grid-cols-[1fr_140px_120px_auto]"><div>Wat</div><div>Wie</div><div>Wanneer</div><div></div></div>
                    {draft.acties.map((a, i) => (
                      <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_140px_120px_auto]" data-testid="overleg-actie-rij">
                        <Input data-testid="overleg-actie-wat" value={a.wat} disabled={!canEdit} onChange={(e) => updActie(i, "wat", e.target.value)} placeholder="Actie" className="h-9" />
                        <Input data-testid="overleg-actie-wie" value={a.wie} disabled={!canEdit} onChange={(e) => updActie(i, "wie", e.target.value)} placeholder="Wie" className="h-9" />
                        <Input data-testid="overleg-actie-wanneer" value={a.wanneer} disabled={!canEdit} onChange={(e) => updActie(i, "wanneer", e.target.value)} placeholder="Wanneer" className="h-9" />
                        {canEdit && <button onClick={() => remActie(i)} className="flex h-9 items-center justify-center rounded-md border border-rose-200 px-2 text-rose-500 hover:bg-rose-50" data-testid="overleg-actie-del"><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Besluiten + volgende */}
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Besluiten</label>
                  <textarea data-testid="overleg-besluiten" value={draft.besluiten} disabled={!canEdit} onChange={(e) => upd("besluiten", e.target.value)} rows={4} className={areaCls} placeholder={"- Besluit 1\n- Besluit 2"} />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Datum / afspraak volgende overleg</label>
                  <textarea data-testid="overleg-volgende" value={draft.volgende} disabled={!canEdit} onChange={(e) => upd("volgende", e.target.value)} rows={4} className={areaCls} placeholder="bijv. donderdag 4 juli 2026, 10:00 — zelfde agenda" />
                </div>
              </div>

              {/* Acties onderaan */}
              <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                {canEdit && <Button data-testid="overleg-save" onClick={save} className="bg-[#1C64B0] hover:bg-[#154E8A]"><Save className="h-4 w-4 mr-1" /> Opslaan</Button>}
                <Button data-testid="overleg-export-pdf" variant="outline" onClick={() => expPDF(draft)}><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
                <Button data-testid="overleg-export-word" variant="outline" onClick={() => expWord(draft)}><FileText className="h-4 w-4 mr-1" /> Word</Button>
                <Button data-testid="overleg-cancel" variant="ghost" onClick={cancel}>Annuleren</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
